// Shared number field inline validation

/**
 * Shows validation error on input field
 * @param {HTMLInputElement} input - The input element
 * @param {HTMLElement|null} fb - The feedback element
 * @param {string} message - The error message
 */
function showError(input, fb, message) {
  input.classList.add('is-invalid');
  if (fb) {
    fb.textContent = message;
    fb.style.display = 'block';
  }
}

/**
 * Clears validation error on input field
 * @param {HTMLInputElement} input - The input element
 * @param {HTMLElement|null} fb - The feedback element
 */
function clearError(input, fb) {
  input.classList.remove('is-invalid');
  if (fb) {
    fb.style.display = 'none';
    fb.textContent = '';
  }
}

/**
 * Validates a number input field based on its data attributes
 * @param {HTMLInputElement} input - The input element to validate
 * @returns {boolean} - True if valid, false otherwise
 */
function validateOne(input) {
  const feedbackSel = input.dataset.feedback || '';
  const fb = feedbackSel ? document.querySelector(feedbackSel) : null;
  const required = String(input.dataset.required || 'false').toLowerCase() === 'true';
  const minAttr = input.dataset.min;
  const min = (minAttr !== undefined && minAttr !== null && minAttr !== '') ? parseInt(minAttr, 10) : null;
  const maxAttr = input.dataset.max;
  const max = (maxAttr !== undefined && maxAttr !== null && maxAttr !== '') ? parseInt(maxAttr, 10) : null;
  const requiredMsg = input.dataset.requiredError || 'This field is required';
  const minMsg = input.dataset.minError || (min !== null ? `Must be at least ${min}` : '');
  const maxMsg = input.dataset.maxError || (max !== null ? `Must not exceed ${max}` : '');

  const raw = (input.value || '').trim();
  const num = raw === '' ? NaN : parseInt(raw, 10);

  // Required check
  if (required && raw === '') {
    showError(input, fb, requiredMsg);
    return false;
  }
  
  // Min check
  if (raw !== '' && min !== null && (isNaN(num) || num < min)) {
    showError(input, fb, minMsg);
    return false;
  }
  
  // Max check
  if (raw !== '' && max !== null && (!isNaN(num) && num > max)) {
    showError(input, fb, maxMsg);
    return false;
  }
  
  // Valid
  clearError(input, fb);
  return true;
}

/**
 * Handles input event to filter out invalid characters without cursor jumping
 * @param {Event} e - The input event
 */
function handleNumberInput(e) {
  const input = e.target;
  const cursorPosition = input.selectionStart;
  const oldValue = input.value;
  const oldLength = oldValue.length;
  
  // Remove any non-digit characters (including -, ., e, +, etc.)
  const newValue = oldValue.replace(/\D/g, '');
  
  // Only update if value changed (prevents cursor jumping on valid input)
  if (newValue !== oldValue) {
    input.value = newValue;
    
    // Calculate new cursor position accounting for removed characters
    const removedChars = oldLength - newValue.length;
    const newCursorPosition = Math.max(0, cursorPosition - removedChars);
    
    // Restore cursor position to prevent jumping
    input.setSelectionRange(newCursorPosition, newCursorPosition);
  }
  
  // Validate after cleaning input
  validateOne(input);
}

/**
 * Handles keypress event to prevent invalid characters from being entered
 * @param {KeyboardEvent} e - The keypress event
 */
function handleKeyPress(e) {
  const char = e.key;
  
  // Allow control keys (backspace, delete, arrow keys, tab, etc.)
  if (e.ctrlKey || e.metaKey || e.altKey || 
      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(char)) {
    return;
  }
  
  // Only allow digits 0-9
  if (!/^\d$/.test(char)) {
    e.preventDefault();
  }
}

/**
 * Handles blur event to enforce min/max constraints
 * @param {Event} e - The blur event
 */
function handleBlur(e) {
  const input = e.target;
  const minAttr = input.dataset.min;
  const min = (minAttr !== undefined && minAttr !== null && minAttr !== '') ? parseInt(minAttr, 10) : null;
  const maxAttr = input.dataset.max;
  const max = (maxAttr !== undefined && maxAttr !== null && maxAttr !== '') ? parseInt(maxAttr, 10) : null;
  
  const value = input.value.trim();
  
  if (value !== '') {
    let num = parseInt(value, 10);
    
    // Enforce minimum value
    if (min !== null && num < min) {
      input.value = min;
    }
    // Enforce maximum value
    else if (max !== null && num > max) {
      input.value = max;
    }
  }
  
  // Validate after enforcing constraints
  validateOne(input);
}

/**
 * Initializes a single number field with validation and input filtering
 * @param {HTMLInputElement} input - The input element to initialize
 */
function initOne(input) {
  if (input.dataset.nfInitialized) return;
  input.dataset.nfInitialized = '1';

  // Remove any existing inline oninput handlers that might conflict
  input.removeAttribute('oninput');
  
  // Add event listeners for proper number input handling
  input.addEventListener('input', handleNumberInput);
  input.addEventListener('keydown', handleKeyPress);
  input.addEventListener('blur', handleBlur);
  input.addEventListener('change', () => validateOne(input));
}

export function initAllNumberFields() {
  document.querySelectorAll('[data-number-field]').forEach(initOne);
}

document.addEventListener('turbolinks:load', initAllNumberFields);
document.addEventListener('DOMContentLoaded', initAllNumberFields);

// Validate all on submit (non-invasive: only mark invalid, do not block here)
document.addEventListener('submit', function(e) {
  const inputs = document.querySelectorAll('[data-number-field]');
  let allValid = true;
  inputs.forEach(inp => { if (!validateOne(inp)) allValid = false; });
  if (!allValid) {
    // Let the page-level submit handler (if any) decide whether to preventDefault
  }
});
